import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import i18n from "src/i18n";
import { useGetPaymentMethods } from "src/api/generic";
import {
  useCreateFoodicsPaymentMethodMapping,
  useDeleteFoodicsPaymentMethodMapping,
} from "src/api/integrations/foodics";
import type {
  FoodicsPaymentMethod,
  FoodicsPaymentMethodMapping,
} from "src/types/foodics";
import { notificationsState } from "src/store/notifications";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";

type PaymentMethodMappingModalProps = {
  open: boolean;
  handleClose: () => void;
  foodicsPaymentMethods: FoodicsPaymentMethod[];
  foodicsPaymentMethodsLoading: boolean;
  mappings: FoodicsPaymentMethodMapping[];
  refetchMappings: () => void;
};

export const PaymentMethodMappingModal = ({
  open,
  handleClose,
  foodicsPaymentMethods,
  foodicsPaymentMethodsLoading,
  mappings,
  refetchMappings,
}: PaymentMethodMappingModalProps) => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [selections, setSelections] = useState<Record<string, number | "">>(
    {}
  );
  const [isSaving, setIsSaving] = useState(false);

  const { data: jazrPaymentMethodsData, isLoading: jazrPaymentMethodsLoading } =
    useGetPaymentMethods();
  const { mutateAsync: createMapping } = useCreateFoodicsPaymentMethodMapping();
  const { mutateAsync: deleteMapping, status: deleteStatus } =
    useDeleteFoodicsPaymentMethodMapping();

  const jazrPaymentMethods = jazrPaymentMethodsData?.data ?? [];
  const currentLang = i18n.language || "en";

  const getFoodicsName = (pm: FoodicsPaymentMethod): string => {
    if (currentLang === "ar" && pm.name_localized) return pm.name_localized;
    return pm.name || "-";
  };

  const mappingByFoodicsId = new Map(
    mappings.map((mapping) => [mapping.external_id, mapping])
  );
  const mappedJazrIds = mappings.map((mapping) => mapping.internal_id);
  const selectedJazrIds = Object.values(selections).filter(
    (value): value is number => value !== ""
  );
  const allMapped =
    foodicsPaymentMethods.length > 0 &&
    foodicsPaymentMethods.every((pm) => mappingByFoodicsId.has(pm.id));
  const hasPendingSelections = selectedJazrIds.length > 0;

  const availableJazrPaymentMethodsFor = (foodicsId: string) =>
    jazrPaymentMethods.filter(
      (pm) =>
        !mappedJazrIds.includes(pm.id) &&
        (!selectedJazrIds.includes(pm.id) ||
          selections[foodicsId] === pm.id)
    );

  const handleSelectionChange = (
    foodicsId: string,
    jazrId: number | ""
  ) => {
    setSelections((prev) => ({ ...prev, [foodicsId]: jazrId }));
  };

  const handleSave = async () => {
    const pendingEntries = Object.entries(selections).filter(
      ([foodicsId, jazrId]) =>
        jazrId !== "" && !mappingByFoodicsId.has(foodicsId)
    );
    if (pendingEntries.length === 0) return;
    setIsSaving(true);
    try {
      for (const [foodicsId, jazrId] of pendingEntries) {
        const foodicsPm = foodicsPaymentMethods.find(
          (pm) => pm.id === foodicsId
        );
        await createMapping({
          internal_id: jazrId as number,
          external_id: foodicsId,
          external_code: foodicsPm?.code,
          external_name: foodicsPm
            ? getFoodicsName(foodicsPm)
            : undefined,
        });
      }
      setNotifications([
        ...notifications,
        { type: "success", message: t("mappings.saveSuccess") },
      ]);
      setSelections({});
      refetchMappings();
    } catch {
      setNotifications([
        ...notifications,
        { type: "error", message: t("mappings.saveError") },
      ]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (mapping: FoodicsPaymentMethodMapping) => {
    try {
      await deleteMapping(mapping.id);
      setNotifications([
        ...notifications,
        { type: "success", message: t("mappings.deleteSuccess") },
      ]);
      refetchMappings();
    } catch {
      setNotifications([
        ...notifications,
        { type: "error", message: t("mappings.deleteError") },
      ]);
    }
  };

  const onClose = () => {
    setSelections({});
    handleClose();
  };

  const isLoading = foodicsPaymentMethodsLoading || jazrPaymentMethodsLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography fontSize={18} fontWeight={600} color="#101828">
          {t("foodicsWorkspace.paymentMethodMappingModal.title")}
        </Typography>
        <Typography fontSize={14} color="#667085">
          {t("foodicsWorkspace.paymentMethodMappingModal.subtitle")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {allMapped && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t("foodicsWorkspace.paymentMethodMappingModal.allMapped")}
          </Alert>
        )}
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                {[
                  t("foodicsWorkspace.paymentMethodMappingModal.foodicsPaymentMethod"),
                  t("foodicsWorkspace.paymentMethodMappingModal.jazrPaymentMethod"),
                  t("mappings.table.actions"),
                ].map((item) => (
                  <TableCell
                    key={item}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      color: "#475467",
                      bgcolor: "#F9FAFB",
                    }}
                  >
                    {item}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableLoadingSkeleton rowsLength={4} cellsLength={3} />
            ) : (
              <TableBody>
                {foodicsPaymentMethods.map((pm) => {
                  const mapping = mappingByFoodicsId.get(pm.id);
                  return (
                    <TableRow key={pm.id}>
                      <TableCell>
                        <Typography fontSize={14} color="#101828">
                          {getFoodicsName(pm)}
                        </Typography>
                        <Typography fontSize={12} color="#667085">
                          {pm.code || pm.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {mapping ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography fontSize={14} color="#101828">
                              {mapping.internal_name || mapping.internal_id}
                            </Typography>
                            <Chip
                              size="small"
                              color="success"
                              label={t(
                                "foodicsWorkspace.paymentMethodMappingModal.mapped"
                              )}
                            />
                          </Stack>
                        ) : (
                          <FormControl size="small" fullWidth>
                            <InputLabel>
                              {t(
                                "foodicsWorkspace.paymentMethodMappingModal.selectJazrPaymentMethod"
                              )}
                            </InputLabel>
                            <Select
                              value={selections[pm.id] ?? ""}
                              label={t(
                                "foodicsWorkspace.paymentMethodMappingModal.selectJazrPaymentMethod"
                              )}
                              onChange={(e) =>
                                handleSelectionChange(
                                  pm.id,
                                  e.target.value as number | ""
                                )
                              }
                            >
                              <MenuItem value="">
                                <em>—</em>
                              </MenuItem>
                              {availableJazrPaymentMethodsFor(pm.id).map(
                                (jazrPm) => (
                                  <MenuItem
                                    key={jazrPm.id}
                                    value={jazrPm.id}
                                  >
                                    {jazrPm.name}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell width={64}>
                        {mapping && (
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deleteStatus === "pending"}
                            onClick={() => handleDelete(mapping)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
          {!isLoading && foodicsPaymentMethods.length === 0 && <NoData />}
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{generalT("cancel")}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasPendingSelections || isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("foodicsWorkspace.paymentMethodMappingModal.saveMappings")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
